//*Libraries imports
import { useState, useEffect, useRef } from "react";
import { Copy, CheckCircle2, AlertCircle } from "lucide-react";
import { useNavigate, useOutletContext, useLocation } from "react-router-dom";

//*Components Imports
import { PaymentInnerHeader } from "./components/shared/PaymentInnerHeader";
import { SalesContractArea } from "./components/shared/SalesContractArea";
import { ProductInfo } from "./components/shared/ProductInfo";
import { PixQRCode } from "./components/pix/PixQRCode";
import { PixTimer } from "./components/pix/PixTimer";
import { PixApprovedModal } from "./components/pix/PixApprovedModal";
import { PixTimeExceededModal } from "./components/pix/PixTimeExceededModal";

//*Types & Schemas Imports
import type { ISaleOutputDTO } from "@/schemas/sales";
import type { PixResponseAsaasDTO } from "@/schemas/asaas/payment";

//*Hooks Imports
import { useCreatePayment } from "@/hooks/asaas/payments/mutations/useCreatePayment";

//*Utils Imports
import { copyToClipboard } from "@/utils/clipboardUtils";

//* Helpers Imports
import { calculatePaymentValues } from "@helpers/paymentCalculator";
import { MethodPaymentEnum } from "@/schemas/methodpayment";
import { isSalePaid } from "./components/shared/utils";

export function PaymentPix() {
  const navigate = useNavigate();
  const location = useLocation();
  const { sale, refetchSale } = useOutletContext<{
    sale: ISaleOutputDTO | null;
    refetchSale: (id: string) => Promise<ISaleOutputDTO | null>;
  }>();
  const customerId = location.state?.customerId;
  const saleId = sale?._id ?? sale?.id ?? null;

  const mutateCreatePayment = useCreatePayment();

  const [pixData, setPixData] = useState<PixResponseAsaasDTO | null>(null);
  const [dueDate, setDueDate] = useState<string | null>(null);
  const [copyFeedback, setCopyFeedback] = useState<string>("");
  const [approvedVisible, setApprovedVisible] = useState<boolean>(false);
  const [timeExceededVisible, setTimeExceededVisible] =
    useState<boolean>(false);
  const [error, setError] = useState<string | null>(null);
  const processedSaleId = useRef<string | null>(null);
  const getExpiration = (
    saleDate?: string,
    asaasExp?: string,
  ): string | null => (saleDate ? `${saleDate}T23:59:59` : (asaasExp ?? null));

  const isPaid = isSalePaid(sale);

  useEffect(() => {
    const initPayment = async () => {
      if (
        !saleId ||
        !customerId ||
        processedSaleId.current === saleId ||
        isPaid
      )
        return;

      const saleDetails = (sale as any)?.paymentDetails;
      const saleDueDate = saleDetails?.dueDate;
      const existingPix = saleDetails?.pixQrCode;
      const pixKey = `pix_payment_created_${saleId}`;

      if (existingPix?.encodedImage) {
        const exp = getExpiration(saleDueDate, existingPix.expirationDate);
        if (exp && new Date(exp).getTime() > Date.now()) {
          setPixData(existingPix);
          setDueDate(exp);
          processedSaleId.current = saleId;
          return;
        }
        sessionStorage.removeItem(pixKey);
      }

      if (sessionStorage.getItem(pixKey)) {
        const lastAttempt = parseInt(sessionStorage.getItem(pixKey) || "0", 10);
        const now = Date.now();
        if (now - lastAttempt < 15000) {
          processedSaleId.current = saleId;
          return;
        }
      }

      processedSaleId.current = saleId;
      sessionStorage.setItem(pixKey, Date.now().toString());

      const calc = calculatePaymentValues({
        sale: sale as ISaleOutputDTO,
        billingType: MethodPaymentEnum.PIX,
      });

      mutateCreatePayment.mutate(
        {
          saleId,
          customer: customerId,
          billingType: "PIX",
          value: calc.finalValue,
          installmentCount: 1,
          totalValue: calc.finalValue,
        },
        {
          onSuccess: (payment) => {
            if (!payment) return;

            window.location.reload();
          },
          onError: (err: any) => {
            if (
              err.code === "PaymentValueMismatch" ||
              err.message?.includes("desatualizados")
            ) {
              window.location.reload();
              return;
            }
            sessionStorage.removeItem(pixKey);
            processedSaleId.current = null;
            setError(err.message || "Erro ao processar pagamento Pix");
          },
        },
      );
    };

    initPayment();
  }, [saleId, customerId, isPaid]);

  useEffect(() => {
    if (isPaid) {
      setApprovedVisible(true);
    }
  }, [isPaid]);

  useEffect(() => {
    if (isPaid || !pixData || !saleId) return;

    const intervalId = setInterval(() => {
      refetchSale(saleId).catch((err) =>
        console.error("Erro ao atualizar status da venda:", err),
      );
    }, 5000);

    return () => clearInterval(intervalId);
  }, [isPaid, pixData, saleId, refetchSale]);

  const handleCopyPix = async () => {
    if (!pixData?.payload) return;

    const success = await copyToClipboard(pixData.payload);
    if (success) {
      setCopyFeedback("Pix copiado com sucesso");
      setTimeout(() => setCopyFeedback(""), 2000);
    }
  };

  const calculateTimeRemaining = (expirationDate: string | null) => {
    if (!expirationDate) return 0;
    const now = new Date().getTime();
    const expiration = new Date(expirationDate).getTime();
    return Math.max(0, expiration - now);
  };

  const handleFinalize = () => {
    setApprovedVisible(false);
    setTimeExceededVisible(false);
    // Bloqueia o retorno e mantém na página
    window.history.pushState(null, "", window.location.href);
  };

  const getProductName = (product: any) => {
    if (!product) return "Produto não encontrado";
    if (typeof product === "object") return product.name;
    const match = product.match(/name:\s*'([^']+)'/);
    return match ? match[1] : product;
  };

  const paymentValues = sale
    ? calculatePaymentValues({
        sale,
        billingType: MethodPaymentEnum.PIX,
      })
    : null;

  return (
    <div className="min-h-screen bg-gray-50 flex flex-col items-center">
      <div className="w-full max-w-[800px] flex flex-col bg-white shadow-sm min-h-screen">
        <PaymentInnerHeader title="Pix" />

        <SalesContractArea />

        <div className="flex flex-col gap-6 px-4 pb-8">
          <div className="bg-[#FCFCFC] rounded-[18px] p-4 border border-[#EDF2F6] flex flex-col gap-4">
            <h2 className="text-lg font-bold text-clarotexto-201">Pix</h2>
            <ProductInfo
              title={sale ? getProductName(sale.productId) : "Carregando..."}
              price={
                paymentValues ? (
                  <div className="flex flex-col items-end">
                    {paymentValues.discountApplied && (
                      <span className="text-sm text-gray-400 line-through">
                        {new Intl.NumberFormat("pt-BR", {
                          style: "currency",
                          currency: "BRL",
                        }).format(paymentValues.originalValue)}
                      </span>
                    )}
                    <span className="text-xl font-bold text-claroprimaria-01">
                      {new Intl.NumberFormat("pt-BR", {
                        style: "currency",
                        currency: "BRL",
                      }).format(paymentValues.finalValue)}
                    </span>
                    {paymentValues.discountApplied && (
                      <span className="text-xs text-green-600 font-medium bg-green-50 px-2 py-0.5 rounded-full mt-1">
                        {paymentValues.discountApplied}%{" "}
                        {sale?.managerDiscount
                          ? "de desconto aplicado"
                          : "de desconto à vista"}
                      </span>
                    )}
                  </div>
                ) : (
                  "..."
                )
              }
            />
          </div>

          <div className="flex flex-col items-center gap-6 py-6 bg-[#FCFCFC] rounded-xl border border-[#EDF2F6]">
            {isPaid ? (
              <div className="flex flex-col items-center gap-4 text-center p-6">
                <div className="size-16 rounded-full bg-green-50 flex items-center justify-center mb-2">
                  <CheckCircle2 className="size-8 text-green-600" />
                </div>
                <h3 className="text-xl font-bold text-clarotexto-201">
                  Pagamento Confirmado
                </h3>
                <p className="text-gray-500 max-w-xs">
                  O pagamento via Pix foi realizado com sucesso.
                </p>
              </div>
            ) : error ? (
              <div className="flex flex-col items-center gap-3 p-6 text-center">
                <AlertCircle className="size-12 text-red-500" />
                <p className="text-red-600 font-medium">{error}</p>
                <button
                  onClick={() => window.location.reload()}
                  className="mt-2 text-sm text-claroprimaria-01 hover:underline"
                >
                  Tentar novamente
                </button>
              </div>
            ) : (
              <>
                <PixQRCode
                  encodedImage={pixData?.encodedImage}
                  loading={mutateCreatePayment.isPending}
                />
                {dueDate && (
                  <div className="w-full max-w-[300px]">
                    <PixTimer
                      totalMs={calculateTimeRemaining(dueDate)}
                      expirationDate={dueDate}
                      onExpire={() => setTimeExceededVisible(true)}
                    />
                  </div>
                )}
                <p className="text-center text-sm text-clarotexto-201 leading-relaxed px-4">
                  Escaneie o QR Code, copie ou
                  <br />
                  envie através de uma das opções abaixo
                </p>
                <button
                  onClick={handleCopyPix}
                  disabled={!pixData?.payload}
                  className="w-full max-w-[450px] flex items-center justify-between p-3 bg-white border border-clarobordas-01 rounded-xl shadow-sm hover:border-claroprimaria-01 transition-colors group disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  <span className="text-sm text-clarotexto-201 truncate px-2 font-medium">
                    {copyFeedback ? (
                      <span className="text-green-600 flex items-center gap-2">
                        <CheckCircle2 className="size-4" /> {copyFeedback}
                      </span>
                    ) : (
                      pixData?.payload || "Gerando código..."
                    )}
                  </span>
                  <div className="size-9 flex items-center justify-center rounded-lg bg-blue-50 group-hover:bg-blue-100 transition-colors">
                    <Copy className="size-5 text-claroprimaria-01" />
                  </div>
                </button>
              </>
            )}

            {!isPaid && (
              <div className="w-full bg-yellow-50 p-4 rounded-lg border border-yellow-100">
                <p className="text-sm text-yellow-800 font-semibold mb-1">
                  Antes de efetuar o pagamento, leia atentamente as regras:
                </p>
                <p className="text-sm text-yellow-700">
                  Não aceitamos depósitos de terceiros, ou seja, o valor
                  depositado deve vir de uma conta com o seu CPF/CNPJ.
                </p>
              </div>
            )}
          </div>
        </div>
      </div>

      <PixApprovedModal
        visible={approvedVisible}
        onClose={() => setApprovedVisible(false)}
        onFinalize={handleFinalize}
      />

      <PixTimeExceededModal
        visible={timeExceededVisible}
        onClose={() => setTimeExceededVisible(false)}
        onGenerateNewPix={() => setTimeExceededVisible(false)}
        onFinalize={handleFinalize}
      />
    </div>
  );
}
